// What the bell can honestly say about the account today — and only that.
//
// The one account read there is: GET /v1/wallets/<primary>/rewards-summary
// (hooks/useRewardsSummary — the Rewards page's and the account menu's own
// read). It is a STATE, not a feed of events: rank, points, the GRAM that can
// be claimed (trading, referrals, creator fees), referral totals, quest
// counts — with no time on any of it. So the bell draws a notice only for what
// asks the account to act and carries no invented moment:
//
//   rewards to claim   when the claimable GRAM is above zero: how much, and
//                      what it is made of; a press opens Rewards
//
// No "new referral" / "rank up" / "quest done" notices: they are events, and
// the read carries none (no time, no list). Making them by comparing one
// reading with the last would print a moment that is only when this browser
// noticed. What the backend would need for them is in the lane's report.

import type { RewardsSummary } from '../../hooks/useRewardsSummary'
import { formatCompactExact } from '../../lib/format'

/** A part of what can be claimed: where it comes from, and how much GRAM. */
export type ClaimPart = { from: string; gram: string }

export type AccountNotice = {
  id: 'rewards-claimable'
  title: string
  /** The GRAM it is about, worded ("1.65"). */
  gram: string
  parts: ClaimPart[]
  /** Where a press on it goes. */
  to: string
}

/** True for a decimal string above zero ("0.40", "12"); false for "0", "0.00",
 *  "", or anything that is not a number. */
function positive(value: string | undefined): boolean {
  if (!value) return false
  const n = Number(value)
  return Number.isFinite(n) && n > 0
}

export function accountNotices(summary: RewardsSummary | null): AccountNotice[] {
  if (!summary || !positive(summary.total_rewards_claimable_gram)) return []
  const parts: ClaimPart[] = [
    { from: 'Trading', gram: summary.trade_rewards_claimable_gram },
    { from: 'Referrals', gram: summary.referral_rewards_claimable_gram },
    { from: 'Creator fees', gram: summary.creator_rewards_claimable_gram },
  ]
    .filter((part) => positive(part.gram))
    .map((part) => ({ from: part.from, gram: formatCompactExact(part.gram) }))
  return [
    {
      id: 'rewards-claimable',
      title: 'Rewards to claim',
      gram: formatCompactExact(summary.total_rewards_claimable_gram),
      parts,
      to: '/rewards',
    },
  ]
}
